import React from 'react';
import { Alert, AlertTitle, Button, Box, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import LaunchIcon from '@mui/icons-material/Launch';
import { warning } from 'src/theme/colors';

interface PatientInfoMissingBannerProps {
  onUpdatePatientInfo: () => void;
  missingPhysician?: boolean;
  missingDateOfBirth?: boolean;
}

const StickyAlert = styled(Alert)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1200,
  marginBottom: theme.spacing(2),
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
  borderRadius: theme.shape.borderRadius,
  '& .MuiAlert-message': {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiAlert-icon': {
    marginRight: theme.spacing(1),
  },
}));

const PatientInfoMissingBanner: React.FC<PatientInfoMissingBannerProps> = ({
  onUpdatePatientInfo,
  missingPhysician = false,
  missingDateOfBirth = false,
}) => {
  const getMissingFields = () => {
    const missing = [];
    if (missingPhysician) missing.push('Physician');
    if (missingDateOfBirth) missing.push('Date of Birth');
    return missing;
  };

  const missingFields = getMissingFields();
  const missingFieldsText =
    missingFields.length === 2
      ? `${missingFields[0]} and ${missingFields[1]}`
      : missingFields[0] || 'required information';
  return (
    <StickyAlert
      severity="warning"
      sx={{
        border: `1px solid ${warning.main}`,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ width: '100%' }}
      >
        <Box>
          <AlertTitle sx={{ margin: 0, fontWeight: 600, fontSize: '1rem' }}>
            Patient Information Missing
          </AlertTitle>
          <Box sx={{ marginTop: 0.5, fontSize: '0.875rem' }}>
            Add {missingFieldsText} to continue with documentation.
          </Box>
        </Box>
        <Button
          variant="outlined"
          color="warning"
          onClick={onUpdatePatientInfo}
          sx={{
            marginLeft: 2,
            backgroundColor: 'white',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
            whiteSpace: 'nowrap',
            fontWeight: 500,
            minWidth: 'auto',
            px: 3,
          }}
          endIcon={<LaunchIcon />}
        >
          Update Patient Info
        </Button>
      </Stack>
    </StickyAlert>
  );
};

export default PatientInfoMissingBanner;
